import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { forwardRef, type HTMLAttributes } from "react";

const colorClassnames = [
  { background: "bg-red-400", text: "text-white" },
  { background: "bg-green-400", text: "text-white" },
  { background: "bg-green-100", text: "text-black-950" },
  { background: "bg-purple-400", text: "text-white" },
  { background: "bg-purple-100", text: "text-black-950" },
  { background: "bg-blue-500", text: "text-white" },
  { background: "bg-blue-200", text: "text-black-950" },
  { background: "bg-purple-600", text: "text-white" },
  { background: "bg-black-200", text: "text-black-950" },

  { background: "bg-blue-700", text: "text-white" },
  { background: "bg-blue-100", text: "text-black-950" },
  { background: "bg-purple-800", text: "text-white" },
  { background: "bg-orange-600", text: "text-white" },
  { background: "bg-orange-400", text: "text-white" },
  { background: "bg-orange-200", text: "text-black-950" },
  { background: "bg-magenta-500", text: "text-white" },
  { background: "bg-magenta-100", text: "text-black-950" },
  { background: "bg-red-700", text: "text-white" },

  { background: "bg-red-200", text: "text-black-950" },
  { background: "bg-green-800", text: "text-white" },
  { background: "bg-yellow-300", text: "text-black-950" },
  { background: "bg-yellow-100", text: "text-black-950" },
];

const avatarVariants = cva(
  "flex shrink-0 items-center justify-center rounded-full",
  {
    variants: {
      size: {
        xs: "size-6 text-xxs-bold",
        sm: "size-8 text-xs-bold",
        md: "size-10 text-sm-bold",
        lg: "size-12 text-md-bold",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

function getColorIndexFromName(firstName: string, lastName: string) {
  const nameKey = `${firstName.toLowerCase()}-${lastName.toLowerCase()}`;
  let hash = 0;

  for (let i = 0; i < nameKey.length; i += 1) {
    const char = nameKey.charCodeAt(i);
    hash = (hash * 31 + char) % Number.MAX_SAFE_INTEGER;
  }

  return Math.abs(hash) % colorClassnames.length;
}

type AimFoxAvatarProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof avatarVariants> & {
    firstName: string;
    lastName: string;
  };
const AimfoxAvatar = forwardRef<HTMLDivElement, AimFoxAvatarProps>(
  ({ size, className, firstName, lastName, ...props }, ref) => {
    const { text, background } =
      colorClassnames[getColorIndexFromName(firstName, lastName)];

    return (
      <div
        ref={ref}
        className={clsx(avatarVariants({ size, className }), text, background)}
        {...props}
      >
        {firstName[0]}
        {lastName[0]}
      </div>
    );
  },
);
AimfoxAvatar.displayName = "AimfoxAvatar";
export default AimfoxAvatar;
